import { useCallback, useState } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { Button, Icon, Menu, Modal, Tab } from "semantic-ui-react"
import { getUserDomain, isCommonDomain } from "simplydo/core/users"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asMutation, asQuery } from "api"
import { useAppSelector } from "store"
import Prompt from "utils/Prompt"
import util from "utils/utils"

import { CheckboxHeader } from "../UI"
import Contact from "./Tabs/Contact"
import { EditOverview } from "./Tabs/EditOverview"
import { Ideas } from "./Tabs/Ideas"
import { Overview } from "./Tabs/Overview"
import { Roles } from "./Tabs/Roles"
import { Settings } from "./Tabs/Settings"
import { Users } from "./Tabs/Users"

type IIdeaBusinessProfileProps = {
  businessProfile: OpenAPI.Schemas["IdeaBusinessProfile"]
  onClose: () => void
  onSave?: (ideaBusinessProfile: OpenAPI.Schemas["IdeaBusinessProfile"]) => void
  onDelete?: (ideaBusinessProfile: OpenAPI.Schemas["IdeaBusinessProfile"]) => void
  config?: {
    openInEditMode?: boolean
    defaultTab?: number
  }
}

const IdeaBusinessProfileModal = ({
  businessProfile: initialProfile,
  onClose,
  onSave,
  onDelete,
  config,
}: IIdeaBusinessProfileProps) => {
  const { t } = useTranslation()

  const profileQuery = useQuery({
    queryKey: ["get", "users", "profiles", initialProfile?._id],
    queryFn: asQuery(api.users.getProfile, { params: [initialProfile?._id] }),
    enabled: !!initialProfile?._id,
    initialData: { ideaBusinessProfile: initialProfile },
    initialDataUpdatedAt: 0, // Force initial stale data to be refetched
    refetchOnMount: true,
  })
  const profileId = profileQuery.data?.ideaBusinessProfile?._id

  const user = useAppSelector((state) => state.user)
  const userDomain = getUserDomain(user)

  const [searchParams, setSearchParams] = useSearchParams()
  const tabActiveIndex = parseInt(searchParams.get("businessProfileTab") || (config?.defaultTab || 0).toString(), 10)

  const canEditProfile = util.hasPermission(user, "ideaBusinessProfile.editDetails", profileId)

  const [isUnsaved, setUnsaved] = useState(false)
  const [isEditing, setIsEditing] = useState(canEditProfile && config?.openInEditMode)

  const onProfileUpdated = (businessProfile) => {
    api.queryClient.setQueryData(["get", "users", "profiles", businessProfile._id], {
      ideaBusinessProfile: businessProfile,
    })
    api.queryClient.invalidateQueries({ queryKey: ["get", "users", "profiles"] })

    setUnsaved(false)
    onSave?.(businessProfile)
  }

  const updateProfileMutation = useMutation({
    mutationKey: ["post", "users", "profiles", profileQuery.data?.ideaBusinessProfile?._id],
    mutationFn: asMutation(api.users.updateProfile),
    onSuccess: ({ ideaBusinessProfile }) => {
      toast.success("Profile updated")
      onProfileUpdated(ideaBusinessProfile)
    },
    onError: () => {
      toast.error("Failed to update profile")
    },
  })

  const updateProfile = useCallback(
    (profile: Partial<OpenAPI.Schemas["IdeaBusinessProfile"]>) => {
      updateProfileMutation.mutate({
        params: [profileId, profile],
      })
    },
    [profileId, updateProfileMutation],
  )

  const updateActiveTab = useCallback(
    (newIndex: number | string) => {
      setSearchParams((prev) => {
        prev.set("businessProfileTab", newIndex.toString())
        return prev
      })
    },
    [setSearchParams],
  )

  const closeModal = useCallback(() => {
    if (isUnsaved) {
      util
        .confirm(
          "Unsaved changes",
          "Unsaved changes will be lost if the modal is closed. Are you sure you want to proceed and discard your changes?",
        )
        .then(() => {
          onClose?.()
          setSearchParams((prev) => {
            prev.delete("businessProfileTab")
            return prev
          })
        })
        .catch(() => {})
    } else {
      onClose?.()
      setSearchParams((prev) => {
        prev.delete("businessProfileTab")
        return prev
      })
    }
  }, [onClose, setSearchParams, isUnsaved])

  if (!profileQuery.isSuccess) return null

  return (
    <Modal
      open={!!profileQuery.data.ideaBusinessProfile}
      dimmer={
        <Modal.Dimmer
          onClick={(e) => {
            if (e.target !== e.currentTarget) {
              return
            }
            closeModal()
          }}
        />
      }
      closeOnDimmerClick={!isUnsaved}
      closeOnEscape={!isUnsaved}
      mountNode={document.getElementById("semantic-modal-mount-node")}
    >
      <Prompt
        when={isUnsaved}
        message="Unsaved changes will be lost if the modal is closed. Are you sure you want to proceed and discard your changes?"
        shouldBlock={(event) => event.nextLocation.pathname !== event.currentLocation.pathname}
      />
      <Modal.Header>{profileQuery.data.ideaBusinessProfile.name}</Modal.Header>
      <Modal.Content scrolling style={{ maxHeight: 600, height: "fit-content" }}>
        {util.hasPermission(user, "ideaBusinessProfile.viewMembers", profileQuery.data.ideaBusinessProfile._id) ||
        util.hasPermission(user, "ideaBusinessProfile.editMembers", profileQuery.data.ideaBusinessProfile._id) ||
        util.hasPermission(user, "ideaBusinessProfile.viewIdeas", profileQuery.data.ideaBusinessProfile._id) ? (
          <Tab
            menu={{ secondary: true, pointing: true }}
            activeIndex={tabActiveIndex}
            onTabChange={(_e, { activeIndex }) => updateActiveTab(activeIndex)}
            style={{ position: "relative" }}
            panes={[
              {
                menuItem: (
                  <Menu.Item>
                    <Icon name="building" />
                    Profile
                  </Menu.Item>
                ),
                render: () =>
                  isEditing ? (
                    <EditOverview businessProfile={profileQuery.data.ideaBusinessProfile} setUnsaved={setUnsaved} />
                  ) : (
                    <Overview businessProfile={profileQuery.data.ideaBusinessProfile} />
                  ),
              },
              util.hasPermission(user, "ideaBusinessProfile.viewDetails", profileQuery.data.ideaBusinessProfile._id) ||
              profileQuery.data.ideaBusinessProfile?.contacts?.length > 0 // There is no straightforward permission for contacts (because challenge admins can see them, but regular users cannot) but the api will only return them if the user has access
                ? {
                    menuItem: (
                      <Menu.Item>
                        <Icon name="address book" />
                        Addresses & Contacts
                        {!profileQuery.data.ideaBusinessProfile.contacts?.length ? (
                          <Icon
                            name="exclamation circle"
                            color="red"
                            size="small"
                            style={{ marginTop: -8, marginLeft: 2, width: 0 }}
                          />
                        ) : null}
                      </Menu.Item>
                    ),
                    render: () => <Contact businessProfile={profileQuery.data.ideaBusinessProfile} onSave={onSave} />,
                  }
                : undefined,
              util.hasPermission(user, "ideaBusinessProfile.viewMembers", profileQuery.data.ideaBusinessProfile._id)
                ? {
                    menuItem: (
                      <Menu.Item>
                        <Icon name="users" />
                        Users
                      </Menu.Item>
                    ),
                    render: () => (
                      <Users businessProfile={profileQuery.data.ideaBusinessProfile} closeModal={closeModal} />
                    ),
                  }
                : undefined,
              util.hasPermission(user, "ideaBusinessProfile.viewIdeas", profileQuery.data.ideaBusinessProfile._id)
                ? {
                    menuItem: (
                      <Menu.Item>
                        <Icon name="lightbulb" />
                        {t("common:capitalise", { key: "generic.ideas" })}
                      </Menu.Item>
                    ),
                    render: () => (
                      <Ideas businessProfile={profileQuery.data.ideaBusinessProfile} closeModal={closeModal} />
                    ),
                  }
                : undefined,
              util.hasPermission(user, "ideaBusinessProfile.deleteProfile", profileQuery.data.ideaBusinessProfile._id)
                ? {
                    menuItem: (
                      <Menu.Item>
                        <Icon name="cogs" />
                        Settings
                      </Menu.Item>
                    ),
                    render: () => (
                      <Settings
                        businessProfile={profileQuery.data.ideaBusinessProfile}
                        updateProfile={updateProfile}
                        closeModal={closeModal}
                        onDelete={onDelete}
                      />
                    ),
                  }
                : undefined,
              util.hasPermission(user, "ideaBusinessProfile.editMembers", profileQuery.data.ideaBusinessProfile._id)
                ? {
                    menuItem: (
                      <Menu.Item
                        style={{
                          position: "absolute",
                          right: 0,
                        }}
                      >
                        <Icon name="lock" />
                        Roles
                      </Menu.Item>
                    ),
                    render: () => (
                      <Roles businessProfile={profileQuery.data.ideaBusinessProfile} closeModal={closeModal} />
                    ),
                  }
                : undefined,
            ]}
          />
        ) : isEditing ? (
          <EditOverview businessProfile={profileQuery.data.ideaBusinessProfile} setUnsaved={setUnsaved} />
        ) : (
          <Overview businessProfile={profileQuery.data.ideaBusinessProfile} />
        )}
      </Modal.Content>
      <Modal.Actions>
        {canEditProfile ? (
          isEditing ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              {userDomain && !isCommonDomain(userDomain) ? (
                <div style={{ display: "flex", textAlign: "left" }}>
                  <CheckboxHeader
                    style={{ marginBottom: 12 }}
                    header={`Open for other @${userDomain} users`}
                    description={`Users with the same domain can join this profile.`}
                    checked={
                      !!profileQuery.data.ideaBusinessProfile.emailDomains?.find(
                        (domain) => domain.domain === userDomain,
                      )
                    }
                    as="h5"
                    onChange={(checked) => {
                      const domains = profileQuery.data.ideaBusinessProfile.emailDomains || []

                      api.queryClient.setQueryData<OpenAPI.GET<"/users/profiles/{business_profile_id}">["response"]>(
                        ["get", "users", "profiles", profileQuery.data.ideaBusinessProfile?._id],
                        (oldData) => ({
                          ideaBusinessProfile: {
                            ...oldData.ideaBusinessProfile,
                            emailDomains: checked
                              ? [...domains, { domain: userDomain }]
                              : domains.filter((d) => d.domain !== userDomain),
                          },
                        }),
                      )
                    }}
                  />
                </div>
              ) : null}
              <div>
                <Button
                  style={{ marginRight: 10 }}
                  onClick={() => {
                    if (isUnsaved) {
                      util
                        .confirm(
                          "Unsaved changes",
                          "Unsaved changes will be lost. Are you sure you want to proceed and discard your changes?",
                        )
                        .then(() => {
                          setUnsaved(false)
                          setIsEditing(false)
                          api.queryClient.setQueryData(
                            ["users", "profiles", profileQuery.data.ideaBusinessProfile._id],
                            profileQuery.data.ideaBusinessProfile,
                          )
                        })
                        .catch(() => {})
                    } else {
                      setUnsaved(false)
                      setIsEditing(false)
                      api.queryClient.setQueryData(
                        ["users", "profiles", profileQuery.data.ideaBusinessProfile._id],
                        profileQuery.data.ideaBusinessProfile,
                      )
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  primary
                  loading={updateProfileMutation.isPending}
                  onClick={() => {
                    updateProfileMutation.mutate(
                      {
                        params: [profileQuery.data.ideaBusinessProfile._id, profileQuery.data.ideaBusinessProfile],
                      },
                      {
                        onSuccess: () => {
                          setIsEditing(false)
                        },
                      },
                    )
                  }}
                >
                  <Icon name="check" />
                  Save changes
                </Button>
              </div>
            </div>
          ) : (
            <Button
              basic
              onClick={() => {
                updateActiveTab(0)
                setIsEditing(true)
              }}
            >
              <Icon name="pencil" />
              Edit profile
            </Button>
          )
        ) : null}
        {isEditing ? null : <Button onClick={closeModal}>Close</Button>}
      </Modal.Actions>
    </Modal>
  )
}

export default IdeaBusinessProfileModal
